import type { ReadingQuestion } from "@examing/types";

export interface EvaluatedSubQuestion {
  question: ReadingQuestion;
  globalIndex: number;
  isAnswered: boolean;
  isCorrect: boolean;
  userAnswer: unknown;
  correctAnswer: unknown;
  pointsEarned: number;
  maxPoints: number;
}

export const normalizeAnswer = (s: unknown): string =>
  String(s ?? "")
    .normalize("NFC")
    .replace(/[‘’ʻʼ`]/g, "'")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

export const asRecord = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};

export function evaluateSubQuestion(
  q: ReadingQuestion,
  globalIndex: number,
  userAnswer: unknown,
  correctAnswer: unknown,
): EvaluatedSubQuestion {
  const maxPoints = typeof q.points === "number" && q.points > 0 ? q.points : 1;
  const qType = q.type || "MULTIPLE_CHOICE";

  switch (qType) {
    case "MULTIPLE_CHOICE":
    case "MULTIPLE_RESPONSE":
    case "TRUE_FALSE": {
      const options = q.options || [];
      const selected = Array.isArray(userAnswer)
        ? userAnswer.map(String)
        : typeof userAnswer === "string" && userAnswer
          ? [userAnswer]
          : [];
      const correctFromRaw = Array.isArray(correctAnswer)
        ? correctAnswer.map(String)
        : typeof correctAnswer === "string" && correctAnswer
          ? [correctAnswer]
          : [];
      const resolvedCorrect =
        correctFromRaw.length > 0
          ? correctFromRaw
          : options
              .filter((opt) => (opt as any).isCorrect === true)
              .map((opt) => String(opt.id));

      const isAnswered = selected.length > 0;
      const isCorrect =
        resolvedCorrect.length > 0 &&
        selected.length === resolvedCorrect.length &&
        new Set(selected).size === selected.length &&
        selected.every((id) => resolvedCorrect.includes(id));

      return {
        question: q,
        globalIndex,
        isAnswered,
        isCorrect,
        userAnswer: selected,
        correctAnswer: resolvedCorrect,
        pointsEarned: isCorrect ? maxPoints : 0,
        maxPoints,
      };
    }

    case "SHORT_ANSWER": {
      const given = typeof userAnswer === "string" ? userAnswer.trim() : "";
      const accepted: string[] =
        q.acceptedAnswers ??
        (typeof correctAnswer === "string" ? [correctAnswer] : []);
      const isAnswered = Boolean(given);
      const isCorrect =
        isAnswered &&
        accepted.some((acc) => normalizeAnswer(acc) === normalizeAnswer(given));

      return {
        question: q,
        globalIndex,
        isAnswered,
        isCorrect,
        userAnswer: given,
        correctAnswer: accepted.join(" / "),
        pointsEarned: isCorrect ? maxPoints : 0,
        maxPoints,
      };
    }

    case "FILL_IN_BLANKS": {
      const blanks = q.blanks || [];
      const givenMap = asRecord(userAnswer);
      if (!blanks.length) {
        const given = typeof userAnswer === "string" ? userAnswer.trim() : "";
        const accepted: string[] =
          q.acceptedAnswers ??
          (typeof correctAnswer === "string" ? [correctAnswer] : []);
        const isAnswered = Boolean(given);
        const isCorrect =
          isAnswered &&
          accepted.some((acc) => normalizeAnswer(acc) === normalizeAnswer(given));
        return {
          question: q,
          globalIndex,
          isAnswered,
          isCorrect,
          userAnswer: given,
          correctAnswer: accepted.join(" / "),
          pointsEarned: isCorrect ? maxPoints : 0,
          maxPoints,
        };
      }

      const isAnswered = blanks.some((b) =>
        Boolean(String(givenMap[b.id] ?? "").trim()),
      );
      const isCorrect =
        blanks.length > 0 &&
        blanks.every((b) => {
          const ans = String(givenMap[b.id] ?? "").trim();
          const accepted = b.acceptedAnswers || [];
          return (
            Boolean(ans) &&
            accepted.some((acc) => normalizeAnswer(acc) === normalizeAnswer(ans))
          );
        });

      return {
        question: q,
        globalIndex,
        isAnswered,
        isCorrect,
        userAnswer: givenMap,
        correctAnswer: blanks.map((b) => b.acceptedAnswers?.[0] ?? "").join(", "),
        pointsEarned: isCorrect ? maxPoints : 0,
        maxPoints,
      };
    }

    case "MATCHING": {
      const pairs = q.matchPairs || [];
      const givenMap = asRecord(userAnswer);
      const isAnswered = pairs.some((p) =>
        Boolean(givenMap[p.key] || givenMap[p.key.trim()]),
      );
      const isCorrect =
        pairs.length > 0 &&
        pairs.every((p) => {
          const val = String(
            givenMap[p.key] ?? givenMap[p.key.trim()] ?? "",
          ).trim();
          return Boolean(val) && normalizeAnswer(val) === normalizeAnswer(p.value);
        });

      return {
        question: q,
        globalIndex,
        isAnswered,
        isCorrect,
        userAnswer: givenMap,
        correctAnswer: pairs.map((p) => `${p.key} → ${p.value}`).join("; "),
        pointsEarned: isCorrect ? maxPoints : 0,
        maxPoints,
      };
    }

    default: {
      const given = userAnswer != null ? String(userAnswer) : "";
      return {
        question: q,
        globalIndex,
        isAnswered: Boolean(given),
        isCorrect: false,
        userAnswer: given,
        correctAnswer: String(correctAnswer ?? ""),
        pointsEarned: 0,
        maxPoints,
      };
    }
  }
}
